// Country dialing prefixes we accept on the login phone input.
// Coverage: NA, full EU/EEA, UK, Switzerland, AU, NZ — every country
// Stripe Connect supports + a few more for completeness. Users in other
// countries can still enter a fully-qualified +XX...XXX number (we keep
// the leading +).
export const PHONE_COUNTRIES = [
  // North America
  { code: 'US', dial: '1',   flag: '🇺🇸', label: 'US' },
  { code: 'CA', dial: '1',   flag: '🇨🇦', label: 'Canada' },
  // British Isles
  { code: 'GB', dial: '44',  flag: '🇬🇧', label: 'UK' },
  { code: 'IE', dial: '353', flag: '🇮🇪', label: 'Ireland' },
  // Western Europe
  { code: 'FR', dial: '33',  flag: '🇫🇷', label: 'France' },
  { code: 'DE', dial: '49',  flag: '🇩🇪', label: 'Germany' },
  { code: 'NL', dial: '31',  flag: '🇳🇱', label: 'Netherlands' },
  { code: 'BE', dial: '32',  flag: '🇧🇪', label: 'Belgium' },
  { code: 'LU', dial: '352', flag: '🇱🇺', label: 'Luxembourg' },
  { code: 'AT', dial: '43',  flag: '🇦🇹', label: 'Austria' },
  { code: 'CH', dial: '41',  flag: '🇨🇭', label: 'Switzerland' },
  // Southern Europe
  { code: 'IT', dial: '39',  flag: '🇮🇹', label: 'Italy' },
  { code: 'ES', dial: '34',  flag: '🇪🇸', label: 'Spain' },
  { code: 'PT', dial: '351', flag: '🇵🇹', label: 'Portugal' },
  { code: 'GR', dial: '30',  flag: '🇬🇷', label: 'Greece' },
  { code: 'CY', dial: '357', flag: '🇨🇾', label: 'Cyprus' },
  { code: 'MT', dial: '356', flag: '🇲🇹', label: 'Malta' },
  // Nordics
  { code: 'SE', dial: '46',  flag: '🇸🇪', label: 'Sweden' },
  { code: 'NO', dial: '47',  flag: '🇳🇴', label: 'Norway' },
  { code: 'DK', dial: '45',  flag: '🇩🇰', label: 'Denmark' },
  { code: 'FI', dial: '358', flag: '🇫🇮', label: 'Finland' },
  { code: 'IS', dial: '354', flag: '🇮🇸', label: 'Iceland' },
  // Central / Eastern Europe
  { code: 'PL', dial: '48',  flag: '🇵🇱', label: 'Poland' },
  { code: 'CZ', dial: '420', flag: '🇨🇿', label: 'Czech Republic' },
  { code: 'SK', dial: '421', flag: '🇸🇰', label: 'Slovakia' },
  { code: 'HU', dial: '36',  flag: '🇭🇺', label: 'Hungary' },
  { code: 'RO', dial: '40',  flag: '🇷🇴', label: 'Romania' },
  { code: 'BG', dial: '359', flag: '🇧🇬', label: 'Bulgaria' },
  { code: 'HR', dial: '385', flag: '🇭🇷', label: 'Croatia' },
  { code: 'SI', dial: '386', flag: '🇸🇮', label: 'Slovenia' },
  // Baltics
  { code: 'EE', dial: '372', flag: '🇪🇪', label: 'Estonia' },
  { code: 'LV', dial: '371', flag: '🇱🇻', label: 'Latvia' },
  { code: 'LT', dial: '370', flag: '🇱🇹', label: 'Lithuania' },
  // Oceania
  { code: 'AU', dial: '61',  flag: '🇦🇺', label: 'Australia' },
  { code: 'NZ', dial: '64',  flag: '🇳🇿', label: 'New Zealand' },
];

const DIAL_CODES = new Set(PHONE_COUNTRIES.map(c => c.dial));

/** Detect whether input is email, phone, or ambiguous. Phone treats both
 *  raw digits and `+`-prefixed E.164 strings as phone. */
export function detectInputType(value) {
  const trimmed = value.trim()
  if (trimmed.includes('@')) return 'email'
  if (trimmed.startsWith('+') && trimmed.replace(/\D/g, '').length >= 8) return 'phone'
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length >= 10) return 'phone'
  return null
}

/**
 * Best-effort default country dial prefix from the browser locale.
 * UK browsers default to '44', US to '1', etc. Falls back to '1' if we
 * can't tell. Used by normalizePhone when the caller doesn't override.
 */
export function defaultDialFromLocale() {
  try {
    const lang = (navigator?.language || 'en-US').toUpperCase();
    const region = lang.includes('-') ? lang.split('-')[1] : '';
    const match = PHONE_COUNTRIES.find(c => c.code === region);
    if (match) return match.dial;
  } catch { /* ignore */ }
  return '1';
}

/**
 * Normalize phone input to E.164 (+44XXXXXXXXXX).
 *
 * @param {string} value Raw input — digits, may include `+`, spaces, dashes.
 * @param {string} [defaultDial] Country dialing prefix (no `+`) to use
 *   if the user didn't include one. Inferred from browser locale when
 *   omitted; falls back to '1'.
 */
export function normalizePhone(value, defaultDial) {
  const outerDial = defaultDial || defaultDialFromLocale();
  const trimmed = String(value).trim();

  // Already E.164-formatted: keep as-is.
  if (trimmed.startsWith('+')) {
    return '+' + trimmed.replace(/\D/g, '');
  }
  const digits = trimmed.replace(/\D/g, '');

  // If the digits already start with the caller's own country dial
  // code (from the picker or browser locale) AND have enough digits
  // after that to be a real number, treat them as already-prefixed.
  // (Catches users who typed "447911..." without the +.)
  //
  // NB: we deliberately do NOT iterate over every known dial code.
  // Doing that mis-routes 10-digit US numbers whose area code starts
  // with 41 (Baltimore, Pittsburgh) as Switzerland, 33 (whatever) as
  // France, etc. The vast majority of users type a local number for
  // their own country — trust the picker/locale, don't guess.
  if (digits.startsWith(outerDial) && digits.length - outerDial.length >= 7) {
    return '+' + digits;
  }

  // Plain local digits — prepend the picker's selected country code
  // (or the locale-inferred default).
  return `+${outerDial}${digits}`;
}

/**
 * Format an E.164 number for display. US numbers get the (555) 555-1234
 * pattern; everything else stays as-is in E.164 — that's the most readable
 * form across regions without pulling in libphonenumber.
 */
export function formatPhoneDisplay(e164) {
  if (!e164) return ''
  const digits = e164.replace(/\D/g, '')
  // US/CA: 11 digits starting with 1 → (555) 555-1234
  if (digits.startsWith('1') && digits.length === 11) {
    const local = digits.slice(1)
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
  }
  // Everywhere else: keep E.164 with a single space after country code
  // for readability (+44 7911123456). Better than nothing.
  for (const dial of DIAL_CODES) {
    if (digits.startsWith(dial) && dial !== '1' && digits.length > dial.length + 6) {
      return `+${dial} ${digits.slice(dial.length)}`
    }
  }
  return e164
}
